import { Icon } from "@material-ui/core";
import React, { useState } from "react";

import { ActionPermissionsObject, LibraryRecentSearchScope, PermissionActionType, WorkspaceResourceFolderType } from "common/interfaces";
import StarterTemplatesPaneController, {
    StarterTemplatesPaneControllerState,
} from "js/controllers/StarterTemplatesPaneController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import { CreatePresentationDialogPaneType } from "js/editor/CreatePresentation/CreatePresentationDialogTypes";
import { ChooseStarterTemplatePane } from "js/editor/CreatePresentation/panes/ChooseStarterTemplatePane";
import {
    formatStarterTemplateCategoryLabel,
} from "js/editor/CreatePresentation/starterTemplates/starterTemplateUtils";
import { PresentationLibraryPaneBreadcrumb } from "js/editor/PresentationLibrary/components/PresentationLibraryPaneBreadcrumb";
import { SearchAndAdd } from "js/editor/PresentationLibrary/components/SearchAndAdd";
import {
    LibraryTemplateGenerateOptions,
    SEARCH_FIELD_ALL,
    TAG_FILTER_ALL,
    TemplatesContent,
    TemplatesListHeaderRow,
    UpgradeIconWrapper,
} from "js/editor/PresentationLibrary/libraryTemplatesPaneShared";
import { t } from "js/i18n";
import { FlexBox } from "js/react/components/LayoutGrid";
import { Layout, PaneBody, PaneContent } from "js/react/views/SharedResources/Components";
import {
    selectedClass,
    SideBarItem,
    SideBarLabel,
} from "js/react/views/SharedResources/Components/WorkspaceResourcesSidebarHelper";
import SideBar from "js/react/views/SharedResources/Components/SideBar";
import { ShowUpgradeDialog } from "js/react/components/Dialogs/BaseDialog";
import { Divider } from "js/Components/Divider";
import { UpgradePlanDialogType } from "js/react/views/MarketingDialogs/UpgradePlanDialog";

const contentFilterOptions = [
    { field: SEARCH_FIELD_ALL, name: t("All Content") },
    { field: "resource:metadata:name", name: t("Name") },
    { field: "resource:tagsText", name: t("Tags") },
];

type StarterTemplatesPaneContentProps = StarterTemplatesPaneControllerState & WorkspaceControllerState & {
    onTemplateChange?: (view: "starter" | "team") => void;
    onCreate: (options: { type: "starter-template"; templateId: string }) => void | Promise<void>;
    onGenerate?: (options: LibraryTemplateGenerateOptions) => void | Promise<void>;
};

function StarterTemplatesPaneContent({
    tags,
    search,
    resourceFolders,
    onTemplateChange,
    onCreate,
    onGenerate,
}: StarterTemplatesPaneContentProps) {
    const [selectedCategory, setSelectedCategory] = useState(TAG_FILTER_ALL);

    const handleSelectTeamTemplates = () => {
        if (onTemplateChange) {
            onTemplateChange("team");
            return;
        }

        AppController.showCreatePresentationDialog({ pane: CreatePresentationDialogPaneType.TEAM_TEMPLATES });
    };

    const handleSearch = (query: string) => {
        const trimmedQuery = query?.trim() ?? "";

        if (trimmedQuery) {
            setSelectedCategory(TAG_FILTER_ALL);
            StarterTemplatesPaneController.setFilters([]);
        }

        StarterTemplatesPaneController.setSearch({
            query: trimmedQuery,
            fields: search.fields,
        });
    };

    const handleContentFilterChange = (event: { target: { value: string } }) => {
        const searchField = event.target.value;
        StarterTemplatesPaneController.setSearch({
            query: search.query,
            fields: searchField === SEARCH_FIELD_ALL ? undefined : [searchField],
        });
    };

    const handleCategoryFilterChange = (category: string) => {
        if (search.query) {
            StarterTemplatesPaneController.setSearch({
                query: "",
                fields: search.fields,
            });
        }

        setSelectedCategory(category);
        StarterTemplatesPaneController.setFilters(
            category === TAG_FILTER_ALL ? [] : [{
                field: "resource.tags",
                operator: "eq",
                value: category,
            }]
        );
    };

    const selectedContentFilter = search.fields?.[0] ?? SEARCH_FIELD_ALL;
    const canUseInspirationSlides = WorkspaceController.actionPermissions[ActionPermissionsObject.INSPIRATION_SLIDES].use;
    const canUpgradeToUseSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.SHARED_RESOURCES,
        PermissionActionType.USE
    );

    const breadcrumb = {
        breadcrumb: selectedCategory === TAG_FILTER_ALL
            ? t("Beautiful.ai Templates")
            : formatStarterTemplateCategoryLabel(selectedCategory),
    };

    return (
        <FlexBox fill vertical top>
            <Layout>
                <SearchAndAdd
                    searchValue={search.query ?? ""}
                    onSearch={handleSearch}
                    searchPlaceholder={t("Search starter templates...")}
                    recentSearchScope={LibraryRecentSearchScope.STARTER_TEMPLATES}
                    contentFilterOptions={contentFilterOptions}
                    selectedContentFilter={selectedContentFilter}
                    onContentFilterChange={handleContentFilterChange}
                />
                <PaneBody>
                    <SideBar
                        readOnly
                        resourceTypes={[]}
                        folderType={WorkspaceResourceFolderType.SHARED}
                        resourceFolders={resourceFolders}
                        selectedFolderId={null}
                    >
                        <SideBarItem
                            onClick={handleSelectTeamTemplates}
                            data-test-id="library-templates-nav-team-templates"
                        >
                            {canUpgradeToUseSharedResources ? <UpgradeIconWrapper onClick={() => ShowUpgradeDialog({
                                type: UpgradePlanDialogType.UPGRADE_PLAN_TO_TEAM,
                                workspaceId: AppController.workspaceId,
                                analytics: { source: "team-templates-pane" }
                            })} />
                                : <Icon className="material-symbols-outlined">domain</Icon>}
                            {t("Team Templates")}
                        </SideBarItem>
                        {canUseInspirationSlides && (
                            <SideBarItem
                                className={selectedClass(true)}
                                data-test-id="library-templates-nav-starter-templates"
                            >
                                <Icon className="material-symbols-outlined">lightbulb</Icon>
                                {t("Beautiful.ai Templates")}
                            </SideBarItem>
                        )}
                        <Divider margin={10} color="#e5e5e5" />
                        <SideBarLabel>
                            <span>{t("Topics")}</span>
                        </SideBarLabel>
                        <SideBarItem
                            className={selectedClass(selectedCategory === TAG_FILTER_ALL)}
                            onClick={() => handleCategoryFilterChange(TAG_FILTER_ALL)}
                            data-test-id="library-templates-nav-all-categories"
                        >
                            {t("All Categories")}
                        </SideBarItem>
                        {tags.map(({ tag }) => (
                            <SideBarItem
                                key={tag}
                                className={selectedClass(selectedCategory === tag)}
                                onClick={() => handleCategoryFilterChange(tag)}
                                data-test-id={`library-templates-nav-category-${tag}`}
                            >
                                {`${formatStarterTemplateCategoryLabel(tag)}`}
                            </SideBarItem>
                        ))}
                    </SideBar>
                    <PaneContent>
                        <TemplatesListHeaderRow>
                            <PresentationLibraryPaneBreadcrumb {...breadcrumb} />
                        </TemplatesListHeaderRow>
                        <TemplatesContent>
                            <ChooseStarterTemplatePane
                                tagFilter={selectedCategory}
                                onCategoryClick={handleCategoryFilterChange}
                                onCreate={onCreate}
                                onGenerate={onGenerate}
                            />
                        </TemplatesContent>
                    </PaneContent>
                </PaneBody>
            </Layout>
        </FlexBox>
    );
}

export const StarterTemplatesPane = StarterTemplatesPaneController.withState(
    WorkspaceController.withState(StarterTemplatesPaneContent)
);
